/**
 * Image Metadata Extraction Utility
 *
 * Uses ExifReader to extract EXIF metadata from images before stripping.
 * Provides formatted display values for GPS, camera info, dates, etc.
 */

import ExifReader from 'exifreader';
import type { MetadataInfo, GPSCoordinates } from '../workers/imageProcessing.types';

/**
 * Extract and format GPS coordinates from EXIF tags
 */
function extractGPS(tags: ExifReader.Tags): GPSCoordinates | null {
  const lat = tags.GPSLatitude;
  const lng = tags.GPSLongitude;
  const latRef = tags.GPSLatitudeRef;
  const lngRef = tags.GPSLongitudeRef;

  if (!lat || !lng) return null;

  // ExifReader provides GPS as array of degrees, minutes, seconds
  // or as a computed decimal value
  let latitude: number;
  let longitude: number;

  if (typeof lat.description === 'number') {
    latitude = lat.description;
  } else if (Array.isArray(lat.value) && lat.value.length >= 3) {
    // Convert DMS to decimal
    const [deg, min, sec] = lat.value;
    latitude = deg + min / 60 + sec / 3600;
  } else {
    return null;
  }

  if (typeof lng.description === 'number') {
    longitude = lng.description;
  } else if (Array.isArray(lng.value) && lng.value.length >= 3) {
    const [deg, min, sec] = lng.value;
    longitude = deg + min / 60 + sec / 3600;
  } else {
    return null;
  }

  // Apply reference (N/S, E/W)
  const latitudeRef = (latRef?.description || latRef?.value || 'N') as 'N' | 'S';
  const longitudeRef = (lngRef?.description || lngRef?.value || 'E') as 'E' | 'W';

  if (latitudeRef === 'S') latitude = -latitude;
  if (longitudeRef === 'W') longitude = -longitude;

  // Format for display
  const latDir = latitude >= 0 ? 'N' : 'S';
  const lngDir = longitude >= 0 ? 'E' : 'W';
  const formatted = `${Math.abs(latitude).toFixed(4)}°${latDir}, ${Math.abs(longitude).toFixed(4)}°${lngDir}`;

  return {
    latitude,
    longitude,
    latitudeRef: latDir as 'N' | 'S',
    longitudeRef: lngDir as 'E' | 'W',
    formatted,
  };
}

/**
 * Extract camera make and model
 */
function extractCamera(tags: ExifReader.Tags): string | null {
  const make = tags.Make?.description || tags.Make?.value;
  const model = tags.Model?.description || tags.Model?.value;

  if (!make && !model) return null;
  if (make && model) {
    // Avoid duplication like "Apple Apple iPhone 15 Pro"
    if (typeof model === 'string' && model.startsWith(String(make))) {
      return model;
    }
    return `${make} ${model}`;
  }
  return String(make || model);
}

/**
 * Extract software/app that created the image
 */
function extractSoftware(tags: ExifReader.Tags): string | null {
  const software = tags.Software?.description || tags.Software?.value;
  return software ? String(software) : null;
}

/**
 * Extract and format date/time
 */
function extractDateTime(tags: ExifReader.Tags): string | null {
  // Try different date fields in order of preference
  const dateFields = [
    'DateTimeOriginal',
    'CreateDate',
    'DateTime',
    'DateTimeDigitized',
  ];

  for (const field of dateFields) {
    const value = tags[field]?.description || tags[field]?.value;
    if (value) {
      // EXIF dates are typically "YYYY:MM:DD HH:MM:SS"
      const str = String(value);
      // Convert to ISO-like format for display
      const formatted = str.replace(/^(\d{4}):(\d{2}):(\d{2})/, '$1-$2-$3');
      return formatted;
    }
  }
  return null;
}

/**
 * Extract AI source/credit information (e.g., "Made with Google AI")
 */
function extractAISource(tags: ExifReader.Tags): string | null {
  // Check various credit/source fields used by AI image generators
  const creditFields = ['Credit', 'credit', 'Creator', 'creator', 'Artist', 'author', 'Author'];

  for (const field of creditFields) {
    const value = tags[field]?.description || tags[field]?.value;
    if (value) {
      const str = String(value);
      // Check if it mentions AI/Google/Gemini
      if (str.toLowerCase().includes('ai') ||
          str.toLowerCase().includes('google') ||
          str.toLowerCase().includes('gemini')) {
        return str;
      }
    }
  }
  return null;
}

/**
 * Extract DigitalSourceType (IPTC field indicating AI-generated content)
 */
function extractDigitalSourceType(tags: ExifReader.Tags): string | null {
  const sourceTypeFields = ['DigitalSourceType', 'digitalsourcetype', 'Digital Source Type'];

  for (const field of sourceTypeFields) {
    const value = tags[field]?.description || tags[field]?.value;
    if (value) {
      return String(value);
    }
  }
  return null;
}

/**
 * Extract image dimensions
 */
function extractDimensions(tags: ExifReader.Tags): { width: number; height: number } {
  const width =
    tags.ImageWidth?.value ||
    tags['Image Width']?.value ||
    tags.PixelXDimension?.value ||
    0;
  const height =
    tags.ImageHeight?.value ||
    tags['Image Height']?.value ||
    tags.PixelYDimension?.value ||
    0;

  return {
    width: typeof width === 'number' ? width : Number(width) || 0,
    height: typeof height === 'number' ? height : Number(height) || 0,
  };
}

/**
 * Build a map of raw tags for display
 */
function buildRawTags(tags: ExifReader.Tags): Record<string, string> {
  const rawTags: Record<string, string> = {};

  // Skip these technical/binary tags that aren't useful for display
  const skipTags = new Set([
    'MakerNote',
    'UserComment',
    'Thumbnail',
    'undefined',
    'StripOffsets',
    'StripByteCounts',
    'JPEGInterchangeFormat',
    'JPEGInterchangeFormatLength',
  ]);

  for (const [key, tag] of Object.entries(tags)) {
    if (skipTags.has(key)) continue;
    if (!tag || typeof tag !== 'object') continue;

    const value = tag.description || tag.value;
    if (value === undefined || value === null) continue;

    // Convert to string for display
    let displayValue: string;
    if (typeof value === 'string') {
      displayValue = value;
    } else if (typeof value === 'number') {
      displayValue = String(value);
    } else if (Array.isArray(value)) {
      displayValue = value.join(', ');
    } else {
      continue; // Skip complex objects
    }

    // Truncate long values
    if (displayValue.length > 100) {
      displayValue = displayValue.slice(0, 97) + '...';
    }

    rawTags[key] = displayValue;
  }

  return rawTags;
}

/**
 * Extract all metadata from an image file
 *
 * @param file - The image file to analyze
 * @returns Formatted metadata information
 */
export async function extractMetadata(file: File): Promise<MetadataInfo> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const tags = await ExifReader.load(arrayBuffer, { expanded: true });

    // Combine all tag groups
    const allTags = {
      ...tags.exif,
      ...tags.file,
      ...tags.iptc,
      ...tags.xmp,
      ...tags.gps,
    } as ExifReader.Tags;

    const gps = extractGPS(allTags);
    const camera = extractCamera(allTags);
    const software = extractSoftware(allTags);
    const dateTime = extractDateTime(allTags);
    const dimensions = extractDimensions(allTags);
    const rawTags = buildRawTags(allTags);
    const aiSource = extractAISource(allTags);
    const digitalSourceType = extractDigitalSourceType(allTags);

    // Determine if image is AI-generated based on metadata
    const isAiGenerated = !!(
      aiSource ||
      digitalSourceType?.toLowerCase().includes('algorithmic') ||
      digitalSourceType?.toLowerCase().includes('trained') ||
      software?.toLowerCase().includes('gemini') ||
      software?.toLowerCase().includes('dall-e') ||
      software?.toLowerCase().includes('midjourney')
    );

    return {
      gps,
      camera,
      software,
      dateTime,
      dimensions,
      tagCount: Object.keys(rawTags).length,
      hasLocationData: gps !== null,
      rawTags,
      aiSource,
      digitalSourceType,
      isAiGenerated,
    };
  } catch (error) {
    // If metadata extraction fails, return empty metadata
    // (image might not have any EXIF data)
    console.warn('Failed to extract metadata:', error);
    return {
      gps: null,
      camera: null,
      software: null,
      dateTime: null,
      dimensions: { width: 0, height: 0 },
      tagCount: 0,
      hasLocationData: false,
      rawTags: {},
      aiSource: null,
      digitalSourceType: null,
      isAiGenerated: false,
    };
  }
}
